import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { ArrowRightIcon, CalendarIcon } from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import { ButtonLink } from "simplydo/react/ui/button";
import { CardContent } from "simplydo/react/ui/card";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Image } from "simplydo/react/ui/image";
import { Label } from "simplydo/react/ui/label";

import api from "api";
import util from "utils/utils";

import {
  HomeRow,
  HomeRowActions,
  HomeRowContainer,
  HomeRowContent,
  HomeRowIcon,
} from "../HomeUI";
import { getDateStatus } from "../dateStatus";
import HomeBlock from "./HomeBlock";

const ExploreChallengesBlock = () => {
  const { t } = useTranslation();

  const challengesQuery = useQuery(initUseQuery(api.users.dashboard, []));

  if (!challengesQuery.data?.challenges?.length) return null;

  return (
    <HomeBlock $width={1}>
      <CardContent>
        <H3>
          {t("home.exploreChallenges.title")}
          <Subheader>
            These {t("generic.challenges")} are highlighted by your
            organisation.
          </Subheader>
        </H3>
        <HomeRowContainer>
          {challengesQuery.data?.challenges?.map((challenge) => {
            const deadlineStatus = getDateStatus(challenge.deadline);
            const isClosed = challenge.stage === "closed";
            const isElapsed = isClosed || Boolean(deadlineStatus?.elapsed);
            return (
              <HomeRow key={challenge._id} data-muted={isElapsed || undefined}>
                <HomeRowIcon>
                  <Image src={util.challengeImage(challenge)} />
                </HomeRowIcon>
                <HomeRowContent>
                  <H4>
                    {challenge.name}
                    <Subheader>
                      {challenge.tagline ? <p>{challenge.tagline}</p> : null}
                      {isClosed ? (
                        <p>
                          <Label color="grey" basic size="small">
                            {t("generic.closed")}
                          </Label>
                        </p>
                      ) : deadlineStatus ? (
                        <p>
                          <Label
                            color={deadlineStatus.color}
                            basic={deadlineStatus.basic}
                            size="small"
                          >
                            <Icon icon={CalendarIcon} />
                            Deadline: {deadlineStatus.text}
                          </Label>
                        </p>
                      ) : null}
                    </Subheader>
                  </H4>
                </HomeRowContent>
                <HomeRowActions>
                  <ButtonLink
                    color="primary"
                    variant="outline"
                    size="small"
                    to={`/challenges/${challenge._id}`}
                  >
                    {t(
                      isElapsed
                        ? "home.exploreChallenges.view"
                        : "home.exploreChallenges.action",
                    )}
                  </ButtonLink>
                </HomeRowActions>
              </HomeRow>
            );
          })}
        </HomeRowContainer>
      </CardContent>
      <CardContent color="primary" textAlign="right" style={{ flexGrow: 0 }}>
        <ButtonLink
          color="primary"
          variant="ghost"
          size="small"
          to="/challenges"
        >
          {t("home.exploreChallenges.viewAll")}
          <Icon icon={ArrowRightIcon} />
        </ButtonLink>
      </CardContent>
    </HomeBlock>
  );
};

export default ExploreChallengesBlock;
